import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService, User } from '@auth0/auth0-angular';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  debounceTime,
  filter,
  shareReplay,
  skipWhile,
  switchMap,
  map,
  take,
  tap,
  startWith,
} from 'rxjs/operators';
import {
  ApiOrganization,
  IApiOrganization,
  IOrganizationInviteData,
} from '../models_new/classes/api-models/ApiOrganization';
import { pagesPATH } from '../models_new/config/pages';
import { LocalStorageKey } from '../models_new/enums/local-storage-keys';
import { OrganizationType } from '../models_new/enums/organization-type';
import { IHasuraUser } from '../models_new/types/hasura-user';
import { OrganizationApiService } from '../services/api/organization-api.service';
import { UserApiService } from '../services/api/user-api.service';
import { ErrorHandlerService } from '../services/error-handler.service';
import {
  LocalStorageService,
  StorageMethod,
} from '../services/local-storage.service';
import { ObjectUtils } from '../utils/object';
import { RXJSUtils } from '../utils/rxjs-utils';
import { RobotBrandType } from '../models_new/enums/robot-brand-types';
import { NotificationService } from '../services/notification.service';

interface IStoredState {
  salesOrg: string;
  customerOrg: string;
}

@Injectable({
  providedIn: 'root',
})
export class StateService {
  /** @prop {Observable<IHasuraUser>} user$ - Active user logged in */
  user$: Observable<IHasuraUser>;
  /** @prop {BehaviorSubject<ApiOrganization>} sales_organization$ - Active sales organization */
  sales_organization$: BehaviorSubject<ApiOrganization> =
    new BehaviorSubject<ApiOrganization>(null);
  /** @prop {BehaviorSubject<ApiOrganization>} customer_organization$ - Active customer organization */
  customer_organization$: BehaviorSubject<ApiOrganization> =
    new BehaviorSubject<ApiOrganization>(null);
  /** @prop {BehaviorSubject<ApiOrganization[]>} organizations$ - List of all organization that the acive user's permisions allow to fetch */
  organizations$: BehaviorSubject<ApiOrganization[]> = new BehaviorSubject<
    ApiOrganization[]
  >(null);
  /** @prop {Observable<IOrganizationInviteData[]>} organizationInvites$ - List of membership invites to other orgs */
  organizationInvites$: Observable<IOrganizationInviteData[]>;
  /** @prop {Observable<boolean>} isUserLogged$ - Indicates if a user is logged in. Used to tell when the access is public */
  isUserLogged$: Observable<boolean>;
  /** @prop {Observable<boolean>} isExportingAllowed$ - Indicates if exporting items is allowed by the current subscription */
  isExportingAllowed$: Observable<boolean>;
  /** @prop {Observable<RobotBrand[]>} activeBrands$ - List of active robot brands */
  activeBrands$: Observable<RobotBrandType[]>;

  // Map of organization id checked for sub status
  private organizationSubStatusMap: Map<string, boolean> = new Map();

  constructor(
    private auth: AuthService,
    private organizationApi: OrganizationApiService,
    private userApi: UserApiService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private errorHandler: ErrorHandlerService,
    private notify: NotificationService
  ) {
    this.user$ = this.auth.user$.pipe(
      skipWhile((user: User) => !user),
      switchMap((u: User) =>
        this.userApi.getUserData(u.sub).pipe(
          map((hasuraUser: IHasuraUser) => {
            if (!hasuraUser) {
              this.errorHandler.navigateToUserErrors([
                new Error('No user with id: ' + u.sub),
              ]);
              return null;
            } else if (
              !hasuraUser.metadata ||
              !hasuraUser.metadata.firstName ||
              !hasuraUser.metadata.lastName ||
              !hasuraUser.metadata.country
            ) {
              this.router.navigate([pagesPATH.USER, pagesPATH.METADATA_EDIT]);
              return null;
            } else {
              const hUser: IHasuraUser = ObjectUtils.cloneObject(hasuraUser);
              hUser.metadata.picture = u.picture ? u.picture : '';
              hUser.id = u.sub;
              return hUser;
            }
          })
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.organizationInvites$ = this.user$.pipe(
      skipWhile((user: IHasuraUser) => !user),
      map((user) => user.id),
      filter(Boolean),
      switchMap((userId) =>
        this.organizationApi.fetchOrganizationInvitesForUser(userId)
      )
    );

    this.user$
      .pipe(
        skipWhile((user: IHasuraUser) => !user),
        switchMap((user: IHasuraUser) => {
          if (user.organization_members.length) {
            return this.organizationApi.getOrganizationsLight();
          } else {
            return of(null);
          }
        }),
        tap((orgs: IApiOrganization[]) => {
          if (!orgs || !orgs.length) {
            this.router.navigate([pagesPATH.USER, pagesPATH.USER_SIGN_UP]);
            return;
          }

          const orgz = this.setOrganizations(orgs);
          const sales_orgs = orgz.filter(
            (f) => f.type === OrganizationType.SALES_ORGANIZATION
          );
          const customer_orgs = orgz.filter(
            (f) => f.type === OrganizationType.CUSTOMER_ORGANIZATION
          );

          const storedSalesOrgId = this.getStoredState?.salesOrg;
          const storedCustomerOrgId = this.getStoredState?.customerOrg;

          const matchingSalesOrg = orgz.find((f) => f.id === storedSalesOrgId);
          const matchingCustomerOrg = orgz.find(
            (f) => f.id === storedCustomerOrgId
          );

          let hasSelectedOrg = false;

          // ---- SALES ORG SELECTION ----
          if (matchingSalesOrg) {
            this.setSalesOrganization(matchingSalesOrg, true);
            hasSelectedOrg = true;
          } else if (sales_orgs.length >= 1) {
            this.setSalesOrganization(sales_orgs[0], true);
            hasSelectedOrg = true;
          }

          // ---- CUSTOMER ORG SELECTION ----
          if (matchingCustomerOrg) {
            this.setCustomerOrganization(matchingCustomerOrg, true);
            hasSelectedOrg = true;
          } else if (customer_orgs.length >= 1 && !hasSelectedOrg) {
            this.setCustomerOrganization(customer_orgs[0]);
            hasSelectedOrg = true;
          }

          // ---- NO ORGS AT ALL ----
          if (!sales_orgs.length && !customer_orgs.length) {
            this.errorHandler.handleError(
              new Error('This user doesn’t have any organizations yet...')
            );
            return;
          }

          // ---- ROUTING DECISION ----
          if (!hasSelectedOrg && sales_orgs.length > 1) {
            // User must choose a sales org manually, but only if no customer org is selected either
            this.router.navigate([pagesPATH.USER, pagesPATH.LANDING_PAGE]);
          }
        })
      )
      .subscribe({
        error: (err) => {
          this.errorHandler.handleError(err);
        },
      });

    /**
     * When navigate back or forward catch which organization is active.
     */
    this.router.events
      .pipe(
        filter(
          (route_event): route_event is NavigationEnd =>
            route_event instanceof NavigationEnd
        ),
        startWith({ url: this.router.url }),
        map((navigation) => {
          // Event states that you are now in a Sales org context
          if (navigation['url'] === '/customers') {
            return this.getStoredState?.salesOrg;
          }
          // Event states that you are now in a Customer org context
          else if (
            navigation['url'] !== '/customers' &&
            navigation['url'].includes('/customers')
          ) {
            const questionMarkIndex = window.location.href.lastIndexOf('?');
            const lastParamIndex: number =
              window.location.href.lastIndexOf('/') + 1;
            const id = window.location.href.substring(
              lastParamIndex,
              questionMarkIndex !== -1
                ? questionMarkIndex
                : window.location.href.length
            );
            return this.getStoredState?.customerOrg || id;
          } else {
            return null;
          }
        }),
        RXJSUtils.filterUndefinedAndNull(),
        switchMap((orgId: string) =>
          this.organizations$.pipe(
            RXJSUtils.filterUndefinedAndNull(),
            map((orgs: ApiOrganization[]) => orgs.find((f) => f.id == orgId))
          )
        ),
        tap((org: ApiOrganization) => {
          if (org) {
            org.type === OrganizationType.SALES_ORGANIZATION
              ? this.setSalesOrganization(org, null, null)
              : this.setCustomerOrganization(org, null, null);
          }
        })
      )
      .subscribe();

    /**
     * Tells whether or not there is a user logged in.
     */
    this.isUserLogged$ = this.auth.user$.pipe(
      map((u) => Boolean(u)),
      take(1),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // Validates and blocks the user with a popup dialog if org subscription
    // doens't exist or is a sub and has expired.
    this.getCustomerOrSalesOrganization()
      .pipe(
        tap((_org: ApiOrganization) => {
          if (window.location.pathname === '/user/sign-up/setup') {
            return;
          }
        })
      )
      .subscribe();

    this.isExportingAllowed$ = this.getCustomerOrSalesOrganization().pipe(
      switchMap((org) => this.isExportEligibleSubscription$(org))
    );

    this.activeBrands$ = this.getCustomerOrSalesOrganization().pipe(
      skipWhile((org) => !org),
      switchMap((org) => this.getRobotBrandTypesByOrg$(org))
    );
  }

  get getStoredState(): IStoredState {
    return this.localStorageService.getData(LocalStorageKey.STATE);
  }

  set setStoredState(state: IStoredState) {
    this.localStorageService.setData(
      LocalStorageKey.STATE,
      state,
      StorageMethod.LOCAL
    );
  }

  customerOrganizationAsPromise(): Promise<ApiOrganization> {
    return firstValueFrom(this.customer_organization$);
  }

  salesOrganizationAsPromise(): Promise<ApiOrganization> {
    return firstValueFrom(this.sales_organization$);
  }

  organizationsAsPromise(): Promise<ApiOrganization[]> {
    return firstValueFrom(this.organizations$);
  }

  setOrganizations(orgs: IApiOrganization[]): ApiOrganization[] {
    const orgz = ObjectUtils.cloneObject(orgs);

    // Convert to ApiOrganization
    const result = orgz.map((m) => {
      const o = new ApiOrganization(m);
      return o;
    });

    this.organizations$.next(result);
    return result;
  }

  checkSubStatus(org: ApiOrganization) {
    // Check if the organization is already checked for subscription status
    if (this.organizationSubStatusMap.has(org.id)) {
      // If it is, we can skip the check
      return;
    }

    if (org.subscription_details && org.valid_sub) {
      const expiresInDays = Math.ceil(
        (new Date(org.subscription_details?.valid_to).getTime() -
          new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      );

      if (expiresInDays < 8 && expiresInDays > 0) {
        this.notify.showMessage(
          `Your subscription expires in ${expiresInDays} days. Please contact support to renew it.`
        );
      }
    }

    if (!org.valid_sub) {
      this.notify.showWarning(
        'Your organization does not have a valid subscription. Please contact support to renew it.'
      );
    }

    this.organizationSubStatusMap.set(org.id, org.valid_sub);
  }

  async setSalesOrganization(
    org: ApiOrganization,
    init: boolean = false,
    navigate: boolean = true
  ) {
    const salesOrg = ObjectUtils.cloneObject(org);
    this.sales_organization$.next(salesOrg);
    this.customer_organization$.next(null);

    this.checkSubStatus(salesOrg);

    if (!init) {
      const customer = await this.customerOrganizationAsPromise();
      this.setStoredState = {
        // Enforce null state if falsy, due to org === null checks
        customerOrg: customer?.id ? customer.id : null,
        salesOrg: salesOrg?.id,
      };

      navigate ? this.router.navigate([pagesPATH.CUSTOMERS]) : null;
    }
  }

  async setOrganizationById(orgId: string) {
    let org = (await this.organizationsAsPromise())?.find(
      (o) => o.id === orgId
    );
    if (!org) {
      this.organizationApi
        .getOrganizationsLight()
        .pipe(
          tap((orgs: IApiOrganization[]) => {
            if (orgs) this.setOrganizations(orgs);
          }),
          switchMap(() => this.organizations$),
          take(1)
        )
        .subscribe({
          next: (orgs: ApiOrganization[]) => {
            org = orgs.find((o) => o.id === orgId);
            if (!org) {
              this.router.navigate([pagesPATH.CUSTOMERS]);
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            } else {
              org.type === OrganizationType.SALES_ORGANIZATION
                ? this.setSalesOrganization(org)
                : this.setCustomerOrganization(org);
            }
            return orgs;
          },
          error: (err) => this.errorHandler.handleError(err),
        });
    } else {
      org.type === OrganizationType.SALES_ORGANIZATION
        ? this.setSalesOrganization(org)
        : this.setCustomerOrganization(org);
    }
  }

  async setCustomerOrganization(
    org: ApiOrganization,
    init: boolean = false,
    navigate: boolean = true
  ) {
    this.customer_organization$.next(org);

    this.checkSubStatus(org);

    if (!init) {
      let sales = (await this.salesOrganizationAsPromise())?.id;

      // No sales organization set, do we have one in stored state?
      if (!sales) {
        sales = this.getStoredState?.salesOrg;
      }
      this.setStoredState = {
        customerOrg: org.id,
        // Enforce null state if falsy, due to org === null checks
        salesOrg: sales ? sales : null,
      };

      navigate ? this.router.navigate([pagesPATH.CUSTOMERS, org.id]) : null;
    }
  }

  getCustomerOrSalesOrganizationPreserveState(): Observable<ApiOrganization> {
    const state = this.getStoredState;
    if (state) {
      return state.customerOrg
        ? this.customer_organization$.pipe(RXJSUtils.filterUndefinedAndNull())
        : this.sales_organization$.pipe(RXJSUtils.filterUndefinedAndNull());
    } else {
      return this.getCustomerOrSalesOrganization();
    }
  }

  // Returns an Observable ApiOrganization that emits whenever customer_organization$ or sales_organization$ emits a non null value
  getCustomerOrSalesOrganization(): Observable<ApiOrganization> {
    return combineLatest([
      this.customer_organization$,
      this.sales_organization$,
    ]).pipe(
      skipWhile(([customer, sales]) => !customer && !sales),
      map(([customer, sales]) => (!customer ? sales : customer)),
      debounceTime(100), // Switching customers causes excess messages, drop them.
      shareReplay({ bufferSize: 1, refCount: false }) // Subscriptions
    );
  }

  private isExportEligibleSubscription$(
    org: ApiOrganization
  ): Observable<boolean> {
    // If any parent organization, validate that.
    if (org?.organization_relations?.length) {
      return this.organizations$.pipe(
        take(1),
        map((orgs: ApiOrganization[]) =>
          orgs.find(
            (f) => f.id === org.organization_relations[0].organization_a_id
          )
        ),
        switchMap((parentOrg: ApiOrganization) =>
          this.isExportEligibleSubscription$(parentOrg)
        )
      );
    } else {
      // Check if the subscription is a trial and hasn't expired and if it allows exporting items.
      const isValid =
        new Date(org.subscription_details?.valid_to) > new Date() &&
        (!org.subscription_details?.trial ||
          org.subscription_details?.subscription_data?.[
            'allow-pattern-export'
          ]);
      return of(isValid);
    }
  }

  private getRobotBrandTypesByOrg$(
    org: ApiOrganization
  ): Observable<RobotBrandType[]> {
    // If it has no robot brands defined itself, check the parent organization.
    if (!org?.robot_brands) {
      return this.organizations$.pipe(
        map(
          (orgs: ApiOrganization[]) =>
            orgs.find(
              (f) =>
                f.id === org?.organization_relations?.[0]?.organization_a_id
            ) || null
        ),
        switchMap((parentOrg: ApiOrganization) =>
          !parentOrg ? of([]) : this.getRobotBrandTypesByOrg$(parentOrg)
        ),
        take(1)
      );
    } else return of(org.robot_brands);
  }
}
